// Default timeout duration is 2 minutes (in milliseconds)
export const TIMEOUT_DURATION_MS = process.env.REACT_APP_TIMEOUT_DURATION_MS 
    ? parseInt(process.env.REACT_APP_TIMEOUT_DURATION_MS) 
    : 2 * 60 * 1000; 

// User data expiration duration is 2 hours (in milliseconds)
export const USER_DATA_EXPIRATION_MS = process.env.REACT_APP_USER_DATA_EXPIRATION_MS
    ? parseInt(process.env.REACT_APP_USER_DATA_EXPIRATION_MS)
    : 5 * 60 * 1000;  // TODO: Change back to 2 * 60 * 60 * 1000 after testing

// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
    'https://apidev.untitledproject.sg';

// Version number
export const APP_VERSION = process.env.REACT_APP_VERSION || '0.1.0';

// Environment
export const APP_ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'Development';

// iOS PWA Debugger Configuration
// Set REACT_APP_SHOW_IOS_PWA_DEBUGGER=true in .env file to enable debugger
export const SHOW_IOS_PWA_DEBUGGER = process.env.REACT_APP_SHOW_IOS_PWA_DEBUGGER === 'true';

// API timeout duration (30 seconds)
export const API_TIMEOUT_MS = process.env.REACT_APP_API_TIMEOUT_MS
    ? parseInt(process.env.REACT_APP_API_TIMEOUT_MS)
    : 30 * 1000;

// Maximum selfie upload size (5MB)
export const MAX_SELFIE_SIZE_KB = process.env.REACT_APP_MAX_SELFIE_SIZE_KB
    ? parseInt(process.env.REACT_APP_MAX_SELFIE_SIZE_KB)
    : 5120;

// API retry configuration
export const API_RETRY_ATTEMPTS = process.env.REACT_APP_API_RETRY_ATTEMPTS
    ? parseInt(process.env.REACT_APP_API_RETRY_ATTEMPTS)
    : 3;

export const API_RETRY_DELAY_MS = process.env.REACT_APP_API_RETRY_DELAY_MS
    ? parseInt(process.env.REACT_APP_API_RETRY_DELAY_MS)
    : 1000;

// Visitor cookie expiration configuration
// Backend sets visitor ID cookie to expire after this duration (default: 2 days)
export const VISITOR_COOKIE_EXPIRATION_MS = process.env.REACT_APP_VISITOR_COOKIE_EXPIRATION_MS
    ? parseInt(process.env.REACT_APP_VISITOR_COOKIE_EXPIRATION_MS)
    : 2 * 24 * 60 * 60 * 1000; // 2 days in milliseconds

// Cookie expiration warning threshold (default: 36 hours)
// Show warning when cookie will expire within this timeframe
export const VISITOR_COOKIE_WARNING_MS = process.env.REACT_APP_VISITOR_COOKIE_WARNING_MS
    ? parseInt(process.env.REACT_APP_VISITOR_COOKIE_WARNING_MS)
    : 36 * 60 * 60 * 1000; // 36 hours in milliseconds

// Visitor ID synchronization interval (default: 2 minutes)
// How often to check and sync visitor ID between cookie and Redux store
export const VISITOR_ID_SYNC_INTERVAL_MS = process.env.REACT_APP_VISITOR_ID_SYNC_INTERVAL_MS
    ? parseInt(process.env.REACT_APP_VISITOR_ID_SYNC_INTERVAL_MS)
    : 2 * 60 * 1000; // 2 minutes in milliseconds

// Zone status polling interval (default: 4 seconds)
// How often to poll zone status for real-time updates in exhibits
export const ZONE_STATUS_POLLING_INTERVAL_MS = process.env.REACT_APP_ZONE_STATUS_POLLING_INTERVAL_MS
    ? parseInt(process.env.REACT_APP_ZONE_STATUS_POLLING_INTERVAL_MS)
    : 4 * 1000; // 4 seconds in milliseconds

// Helper functions for cookie expiration
export const getCookieExpirationHours = (): number => {
    return Math.floor(VISITOR_COOKIE_EXPIRATION_MS / (1000 * 60 * 60));
};

export const getCookieWarningHours = (): number => {
    return Math.floor(VISITOR_COOKIE_WARNING_MS / (1000 * 60 * 60));
};

// Helper function for sync interval
export const getSyncIntervalMinutes = (): number => {
    return Math.floor(VISITOR_ID_SYNC_INTERVAL_MS / (1000 * 60));
};

// Helper function for zone polling interval
export const getZonePollingIntervalSeconds = (): number => {
    return Math.floor(ZONE_STATUS_POLLING_INTERVAL_MS / 1000);
};

// Age Groups for visitor registration API
export const AGE_GROUPS = [
    { value: 'age_under_18', label: 'Under 18', min: 0, max: 17 },
    { value: 'age_18_25', label: '18-25', min: 18, max: 25 },
    { value: 'age_26_45', label: '26-45', min: 26, max: 45 },
    { value: 'age_46_65', label: '46-65', min: 46, max: 65 },
    { value: 'age_above_65', label: 'Over 65', min: 66, max: 120 },
    { value: 'age_undisclosed', label: 'Undisclosed', min: 999, max: 999},
] as const;

export type AgeGroupValue = typeof AGE_GROUPS[number]['value'];

// Helper function to get age group from numeric age
export const getAgeGroupFromAge = (age: number): AgeGroupValue => {
    const ageGroup = AGE_GROUPS.find(group => age >= group.min && age <= group.max);
    return ageGroup?.value || 'age_under_18';
};

// Helper function to get age group label
export const getAgeGroupLabel = (ageGroupValue: AgeGroupValue): string => {
    const ageGroup = AGE_GROUPS.find(group => group.value === ageGroupValue);
    return ageGroup?.label || 'Unknown';
};

// Country code mapping for API (maps display name to ISO country code)
export const COUNTRY_CODE_MAP: Record<string, string> = {
    "Singapore": "SG",
    "Malaysia": "MY",
    "Indonesia": "ID",
    "Thailand": "TH",
    "Philippines": "PH",
    "Vietnam": "VN",
    "Myanmar": "MM",
    "Cambodia": "KH",
    "Laos": "LA",
    "Brunei": "BN",
    "China": "CN",
    "Taiwan": "TW",
    "Hong Kong": "HK",
    "Macau": "MO",
    "Japan": "JP",
    "South Korea": "KR",
    "North Korea": "KP",
    "India": "IN",
    "Pakistan": "PK",
    "Bangladesh": "BD",
    "Sri Lanka": "LK",
    "Nepal": "NP",
    "Bhutan": "BT",
    "Maldives": "MV",
    "Afghanistan": "AF",
    "Australia": "AU",
    "New Zealand": "NZ",
    "United States": "US",
    "Canada": "CA",
    "Mexico": "MX",
    "United Kingdom": "GB",
    "France": "FR",
    "Germany": "DE",
    "Italy": "IT",
    "Spain": "ES",
    "Netherlands": "NL",
    "Belgium": "BE",
    "Switzerland": "CH",
    "Austria": "AT",
    "Denmark": "DK",
    "Sweden": "SE",
    "Norway": "NO",
    "Finland": "FI",
    "Poland": "PL",
    "Czech Republic": "CZ",
    "Hungary": "HU",
    "Slovakia": "SK",
    "Slovenia": "SI",
    "Croatia": "HR",
    "Serbia": "RS",
    "Montenegro": "ME",
    "Bosnia & Herzegovina": "BA",
    "Albania": "AL",
    "Macedonia": "MK",
    "Bulgaria": "BG",
    "Romania": "RO",
    "Greece": "GR",
    "Turkey": "TR",
    "Cyprus": "CY",
    "Malta": "MT",
    "Russia": "RU",
    "Ukraine": "UA",
    "Belarus": "BY",
    "Lithuania": "LT",
    "Latvia": "LV",
    "Estonia": "EE",
    "Brazil": "BR",
    "Argentina": "AR",
    "Chile": "CL",
    "Peru": "PE",
    "Colombia": "CO",
    "Venezuela": "VE",
    "Ecuador": "EC",
    "Bolivia": "BO",
    "Paraguay": "PY",
    "Uruguay": "UY",
    "Guyana": "GY",
    "Suriname": "SR",
    "South Africa": "ZA",
    "Egypt": "EG",
    "Nigeria": "NG",
    "Kenya": "KE",
    "Ethiopia": "ET",
    "Ghana": "GH",
    "Morocco": "MA",
    "Algeria": "DZ",
    "Tunisia": "TN",
    "Libya": "LY",
    "Sudan": "SD",
    "South Sudan": "SS",
    "Chad": "TD",
    "Niger": "NE",
    "Mali": "ML",
    "Burkina Faso": "BF",
    "Ivory Coast": "CI",
    "Guinea": "GN",
    "Senegal": "SN",
    "Gambia": "GM",
    "Guinea-Bissau": "GW",
    "Sierra Leone": "SL",
    "Liberia": "LR",
    "Mauritania": "MR",
    "Cape Verde": "CV",
    "São Tomé & Príncipe": "ST",
    "Equatorial Guinea": "GQ",
    "Gabon": "GA",
    "Cameroon": "CM",
    "Central African Republic": "CF",
    "Congo": "CG",
    "DR Congo": "CD",
    "Angola": "AO",
    "Zambia": "ZM",
    "Zimbabwe": "ZW",
    "Botswana": "BW",
    "Namibia": "NA",
    "Lesotho": "LS",
    "Eswatini": "SZ",
    "Mozambique": "MZ",
    "Madagascar": "MG",
    "Mauritius": "MU",
    "Seychelles": "SC",
    "Comoros": "KM",
    "Djibouti": "DJ",
    "Eritrea": "ER",
    "Somalia": "SO",
    "Uganda": "UG",
    "Tanzania": "TZ",
    "Rwanda": "RW",
    "Burundi": "BI",
    "Malawi": "MW",
    // If country not found, default to "--" (unknown/other)
};

// Helper function to get country code for API
export const getCountryCodeForAPI = (countryName: string): string => {
    return COUNTRY_CODE_MAP[countryName] || "--";
};

// Reverse mapping: convert country code back to country name for display
export const CODE_TO_COUNTRY_MAP: Record<string, string> = Object.entries(COUNTRY_CODE_MAP)
    .reduce((acc, [countryName, countryCode]) => {
        acc[countryCode] = countryName;
        return acc;
    }, {} as Record<string, string>);

// Helper function to get country name from country code for display
export const getCountryNameFromCode = (countryCode: string): string => {
    return CODE_TO_COUNTRY_MAP[countryCode] || countryCode;
};

// Array of country names for the nationality dropdown
export const COUNTRIES = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua & Barbuda", "Argentina", "Armenia", "Australia", "Austria",
    "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia",
    "Bosnia & Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada",
    "Cape Verde", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia",
    "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "DR Congo", "Ecuador", "Egypt",
    "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia",
    "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras",
    "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Ivory Coast", "Jamaica", "Japan",
    "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia",
    "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta",
    "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco",
    "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria",
    "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay",
    "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts & Nevis", "Saint Lucia",
    "Saint Vincent & the Grenadines", "Samoa", "San Marino", "São Tomé & Príncipe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles",
    "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan",
    "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand",
    "Timor-Leste", "Togo", "Tonga", "Trinidad & Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine",
    "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela",
    "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];